import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to format API error details
function formatApiErrorDetail(error) {
    // Extract detail from Axios error response
    const detail = error?.response?.data?.detail;
    const status = error?.response?.status;
    
    if (detail != null) {
        if (typeof detail === "string") return detail;
        if (Array.isArray(detail))
            return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
        if (detail && typeof detail.msg === "string") return detail.msg;
        return String(detail);
    }
    
    // No detail from server — provide meaningful fallback based on status
    if (status === 401) return "Credenciales inválidas";
    if (status === 403) return "Acceso denegado";
    if (status === 429) return "Demasiados intentos. Intenta en 15 minutos.";
    if (status >= 500) return "Error del servidor. Intenta de nuevo.";
    
    // Network error (no response at all)
    if (!error?.response) return "Error de conexión. Verifica tu internet e intenta de nuevo.";
    
    return "Algo salió mal. Por favor intenta de nuevo.";
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const isRefreshing = useRef(false);
    const refreshQueue = useRef([]);

    const checkAuth = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/auth/me`, {
                withCredentials: true
            });
            setUser(data);
        } catch {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    // Axios interceptor for automatic token refresh on 401
    useEffect(() => {
        const interceptorId = axios.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                // Only attempt refresh for 401 errors, not on auth endpoints, and not already retried
                if (
                    error.response?.status === 401 &&
                    !originalRequest._retry &&
                    !originalRequest.url?.includes('/api/auth/login') &&
                    !originalRequest.url?.includes('/api/auth/refresh') &&
                    !originalRequest.url?.includes('/api/auth/me')
                ) {
                    originalRequest._retry = true;

                    if (isRefreshing.current) {
                        // Queue the request while refresh is in progress
                        return new Promise((resolve, reject) => {
                            refreshQueue.current.push({ resolve, reject });
                        }).then(() => axios(originalRequest));
                    }

                    isRefreshing.current = true;

                    try {
                        await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
                        // Refresh succeeded, retry queued requests
                        refreshQueue.current.forEach(({ resolve }) => resolve());
                        refreshQueue.current = [];
                        isRefreshing.current = false;
                        return axios(originalRequest);
                    } catch {
                        // Refresh failed — session is truly expired
                        refreshQueue.current.forEach(({ reject }) => reject(error));
                        refreshQueue.current = [];
                        isRefreshing.current = false;
                        setUser(false);
                        return Promise.reject(error);
                    }
                }

                return Promise.reject(error);
            }
        );

        return () => {
            axios.interceptors.response.eject(interceptorId);
        };
    }, []);

    const login = async (email, password) => {
        try {
            const { data } = await axios.post(
                `${API_URL}/api/auth/login`,
                { email, password },
                { withCredentials: true }
            );
            setUser(data);
            return { success: true, data };
        } catch (error) {
            const message = formatApiErrorDetail(error);
            return { success: false, error: message };
        }
    };

    const logout = async () => {
        try {
            await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
        } catch {
            // silent - logout failure is non-blocking
        } finally {
            setUser(false);
        }
    };

    const changePassword = async (newPassword) => {
        try {
            await axios.post(
                `${API_URL}/api/auth/change-password`,
                { new_password: newPassword },
                { withCredentials: true }
            );
            setUser(prev => prev ? { ...prev, must_change_password: false } : prev);
            return { success: true };
        } catch (error) {
            const message = formatApiErrorDetail(error);
            return { success: false, error: message };
        }
    };

    const forgotPassword = async (email) => {
        try {
            const { data } = await axios.post(
                `${API_URL}/api/auth/forgot-password`,
                { email }
            );
            return { success: true, message: data.message };
        } catch (error) {
            const message = formatApiErrorDetail(error);
            return { success: false, error: message };
        }
    };

    const resetPassword = async (token, newPassword) => {
        try {
            const { data } = await axios.post(
                `${API_URL}/api/auth/reset-password`,
                { token, new_password: newPassword }
            );
            return { success: true, message: data.message };
        } catch (error) {
            const message = formatApiErrorDetail(error);
            return { success: false, error: message };
        }
    };

    const value = {
        user,
        loading,
        login,
        logout,
        changePassword,
        forgotPassword,
        resetPassword,
        checkAuth,
        isAuthenticated: !!user && user !== false,
        isMentor: user?.role === 'mentor',
        isUsuario: user?.role === 'usuario',
        mustChangePassword: user?.must_change_password
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export default AuthContext;
